import { useEffect, useRef } from 'react';

/**
 * Keeps the screen awake and the tab unfrozen while `active` is true.
 *
 * WHY: browsers throttle JS timers and freeze inactive tabs to save battery.
 * For features relying on a long-lived WebSocket (JP search, doc-gen, etc.),
 * a throttled tab can miss heartbeats and lose the connection. Wake Lock tells
 * the OS: "don't sleep, I have important work in flight".
 *
 * Behavior:
 * - `active=true`  → request a screen wake lock
 * - `active=false` → release the lock
 * - Unmount → release the lock (safety)
 * - Browser auto-releases on visibility loss → re-requests on return
 * - No-op if Wake Lock API unavailable (Firefox < 126, Safari < 16.4)
 *
 * Generic by design: use for any long-running operation, not just JP search.
 */
export function useWakeLock(active: boolean): void {
  const lockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!active) {
      if (lockRef.current && !lockRef.current.released) {
        void lockRef.current.release();
        lockRef.current = null;
      }
      return;
    }

    // Feature detect — graceful degradation
    if (!('wakeLock' in navigator)) {
      return;
    }

    let cancelled = false;

    const requestLock = async () => {
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) {
          void lock.release();
          return;
        }
        // Defense-in-depth: if a previous request is still referenced in
        // lockRef (possible when mount + visibilitychange race and both
        // resolve with visibilityState === 'visible' before the UA's
        // auto-release-on-hidden fires), release it before overwriting to
        // avoid an orphaned sentinel. Browsers typically auto-release on
        // hidden, but non-mainstream engines (Electron, legacy WebView)
        // may lag on spec compliance — this guard is trivial insurance.
        if (lockRef.current && !lockRef.current.released) {
          void lockRef.current.release();
        }
        lockRef.current = lock;
      } catch (err) {
        // Silent: can reject if page is hidden at request time, or battery saver.
        // Not critical — visibilitychange handler will retry.
        console.warn('[useWakeLock] request failed', err);
      }
    };

    void requestLock();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && active) {
        void requestLock();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (lockRef.current && !lockRef.current.released) {
        void lockRef.current.release();
        lockRef.current = null;
      }
    };
  }, [active]);
}
